import { useEffect, useState, useRef } from 'react';
import { financialRecoveryService, GET_RECOVERED_CASES } from '@/api';
import { Combobox, Options } from '@/components/ui/combobox';
import { useQuery } from '@tanstack/react-query';
import Icons from '@/const/icons';
import { Icon, LabelShowingResults } from '@/components/common';
import { useRouter } from 'next/router';

interface Props {
	filterValue: string | undefined;
	handleFilter: (value: string | undefined) => void;
	total?: number;
	totalItems?: number;
	paramSize: number;
}

export const FinancialFilter: React.FC<Props> = ({
	filterValue,
	handleFilter,
	total,
	totalItems,
	paramSize,
}) => {
	const router = useRouter();
	const { year } = router.query;
	const [isOpen, setIsOpen] = useState(false);

	const baseOptions = useRef<Options[]>([
		{
			label: 'Current Year',
			value: '',
			icon: (
				<Icon
					src={Icons.calendar}
					width={24}
					height={24}
					className="text-primary"
				/>
			),
		},
		{
			label: 'Last Year',
			value: '',
			icon: (
				<Icon
					src={Icons.calendar2}
					width={24}
					height={24}
					className="text-primary"
				/>
			),
		},
	]);

	const [options, setOptions] = useState<Options[]>(baseOptions.current);
	const isInitialized = useRef(false);

	const { data: filters } = useQuery({
		queryKey: [GET_RECOVERED_CASES],
		queryFn: () => financialRecoveryService.getFilters(),
	});

	useEffect(() => {
		if (filters?.data && !isInitialized.current) {
			baseOptions.current[0].value = String(filters.data.currentYear);
			baseOptions.current[1].value = String(filters.data.lastYear);
			const data = filters.data.years?.map((year) => ({
				label: year,
				value: String(year),
				icon: (
					<Icon
						src={Icons.calendarEight}
						width={24}
						height={24}
						className="text-primary"
					/>
				),
			}));

			let merged = [...baseOptions.current];
			if (data && data.length > 0) {
				merged = [...merged, ...data];
			}

			const withClear: Options[] = [
				{
					value: 'clear-all',
					label: 'Clear All',
					icon: (
						<Icon
							src={Icons.clear}
							width={24}
							height={24}
							className="text-primary"
						/>
					),
				},
				...merged,
			];
			setOptions(withClear);
			isInitialized.current = true;
		}
	}, [filters, year]);

	const setFilter = (value?: string) => {
		handleFilter(value);
	};

	return (
		<div className="flex items-center gap-x-2">
			<div className="max-w-max">
				<Combobox
					open={isOpen}
					setOpen={setIsOpen}
					value={filterValue}
					setValue={setFilter}
					placeholder="View All"
					options={options}
					classNameTrigger="bg-white h-[33px] md:h-auto"
					classNameCommandGroup="w-[177px]"
				/>
			</div>
			<LabelShowingResults
				total={total || 0}
				totalItems={totalItems || 0}
				labelTotalItems="Resolved Cases"
				keepLabelTotalItems={true}
				isSearched={paramSize > 0}
			/>
		</div>
	);
};
